import { useCallback, useMemo, useState } from "react";

export function useSignupValidation({ step, stepFieldsMap, getFieldMessage }) {
    const [fieldErrors, setFieldErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const getStepFields = useCallback((targetStep = step) => {
        return stepFieldsMap[targetStep] || [];
    }, [step, stepFieldsMap]);

    const passwordRelatedFields = useMemo(() => new Set(["password", "pwdConfirm"]), []);

    const validateFields = useCallback((fields) => {
        const normalizedErrors = {};
        let hasErrors = false;

        fields.forEach((fieldName) => {
            const message = getFieldMessage(fieldName);
            normalizedErrors[fieldName] = message;
            if (message) hasErrors = true;
        });

        setFieldErrors((prev) => ({ ...prev, ...normalizedErrors }));
        return !hasErrors;
    }, [getFieldMessage]);

    const handleFieldBlur = useCallback((fieldName) => {
        setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
        setFieldErrors((prev) => {
            const nextErrors = { ...prev, [fieldName]: getFieldMessage(fieldName) };

            // Keep password confirmation state in sync when either field is re-blurred.
            if (passwordRelatedFields.has(fieldName)) {
                const pairedFieldName = fieldName === "password" ? "pwdConfirm" : "password";
                if (touchedFields[pairedFieldName]) {
                    nextErrors[pairedFieldName] = getFieldMessage(pairedFieldName);
                }
            }

            return nextErrors;
        });
    }, [getFieldMessage, passwordRelatedFields, touchedFields]);

    const showFieldError = useCallback((fieldName) => {
        return Boolean((touchedFields[fieldName] || attemptedSubmit) && fieldErrors[fieldName]);
    }, [attemptedSubmit, fieldErrors, touchedFields]);

    const markFieldsTouched = useCallback((fields) => {
        setTouchedFields((prev) => {
            const nextTouched = { ...prev };
            fields.forEach((fieldName) => {
                nextTouched[fieldName] = true;
            });
            return nextTouched;
        });
    }, []);

    return {
        fieldErrors,
        touchedFields,
        attemptedSubmit,
        setFieldErrors,
        setTouchedFields,
        setAttemptedSubmit,
        getStepFields,
        validateFields,
        handleFieldBlur,
        showFieldError,
        markFieldsTouched,
    };
}
